import { useState, useEffect, useRef, useCallback } from "react";

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA",
  "KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ",
  "NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT",
  "VA","WA","WV","WI","WY","DC",
];

const STATE_NAMES = {
  AL:"Alabama", AK:"Alaska", AZ:"Arizona", AR:"Arkansas", CA:"California",
  CO:"Colorado", CT:"Connecticut", DE:"Delaware", FL:"Florida", GA:"Georgia",
  HI:"Hawaii", ID:"Idaho", IL:"Illinois", IN:"Indiana", IA:"Iowa",
  KS:"Kansas", KY:"Kentucky", LA:"Louisiana", ME:"Maine", MD:"Maryland",
  MA:"Massachusetts", MI:"Michigan", MN:"Minnesota", MS:"Mississippi", MO:"Missouri",
  MT:"Montana", NE:"Nebraska", NV:"Nevada", NH:"New Hampshire", NJ:"New Jersey",
  NM:"New Mexico", NY:"New York", NC:"North Carolina", ND:"North Dakota", OH:"Ohio",
  OK:"Oklahoma", OR:"Oregon", PA:"Pennsylvania", RI:"Rhode Island", SC:"South Carolina",
  SD:"South Dakota", TN:"Tennessee", TX:"Texas", UT:"Utah", VT:"Vermont",
  VA:"Virginia", WA:"Washington", WV:"West Virginia", WI:"Wisconsin", WY:"Wyoming",
  DC:"Washington D.C.",
};

export default function SchoolPickerModal({ onSelect, onClose }) {
  const [step,       setStep]       = useState(1); // 1=state, 2=search
  const [stateQuery, setStateQuery] = useState("");
  const [selectedState, setSelectedState] = useState("");
  const [nameQuery,  setNameQuery]  = useState("");
  const [results,    setResults]    = useState([]);
  const [loading,    setLoading]    = useState(false);
  const [searched,   setSearched]   = useState(false);
  const debounceRef = useRef(null);
  const nameInputRef = useRef(null);

  // Filter state list by what user types
  const filteredStates = US_STATES.filter(s =>
    !stateQuery ||
    s.toLowerCase().includes(stateQuery.toLowerCase()) ||
    STATE_NAMES[s]?.toLowerCase().includes(stateQuery.toLowerCase())
  );

  const search = useCallback(async (name, state) => {
    if (name.length < 2) { setResults([]); setSearched(false); return; }
    setLoading(true);
    setSearched(true);
    try {
      const params = new URLSearchParams({ name, state, limit: "30" });
      const res    = await fetch(`/api/schools?${params}`);
      const data   = await res.json();
      setResults(data.schools || []);
    } catch (e) {
      console.error("School search error:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (step !== 2) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      search(nameQuery, selectedState);
    }, 250); // faster debounce since state pre-filters
    return () => clearTimeout(debounceRef.current);
  }, [nameQuery, selectedState, step, search]);

  // Focus name input when moving to step 2
  useEffect(() => {
    if (step === 2) setTimeout(() => nameInputRef.current?.focus(), 100);
  }, [step]);

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  const handleBackdrop = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const selectState = (s) => {
    setSelectedState(s);
    setStep(2);
    setNameQuery("");
    setResults([]);
    setSearched(false);
  };

  return (
    <div
      onClick={handleBackdrop}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(15,23,42,0.6)", backdropFilter: "blur(4px)" }}
    >
      <div
        className="bg-white rounded-3xl shadow-2xl w-full max-w-lg flex flex-col"
        style={{ maxHeight: "85vh" }}
      >

        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-slate-100">
          <div>
            <h3 className="text-xl font-bold text-slate-800">
              {step === 1 ? "Select Your State 📍" : `Search in ${STATE_NAMES[selectedState]} 🏫`}
            </h3>
            <p className="text-xs text-slate-400 mt-0.5">
              {step === 1
                ? "Pick your state first to speed up the search"
                : "Type your school name to find it"}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 text-2xl leading-none transition-colors"
          >×</button>
        </div>

        {/* Step 1 — State picker */}
        {step === 1 && (
          <>
            <div className="px-6 py-4">
              <input
                autoFocus
                value={stateQuery}
                onChange={e => setStateQuery(e.target.value)}
                placeholder="Type state name or abbreviation..."
                className="w-full p-3.5 rounded-2xl border-2 border-slate-100 focus:border-amber-400 outline-none transition-all placeholder:text-slate-300 text-sm bg-slate-50"
              />
            </div>
            <div className="flex-1 overflow-y-auto px-6 pb-6">
              <div className="grid grid-cols-5 sm:grid-cols-8 gap-2">
                {filteredStates.map(s => (
                  <button
                    key={s}
                    onClick={() => selectState(s)}
                    className="flex items-center justify-center p-2 rounded-2xl border-2 border-slate-100 hover:border-amber-400 hover:bg-amber-50 transition-all text-left group"
                  >
                    <span className="text-lg font-black text-slate-300 group-hover:text-amber-400 w-8 transition-colors">
                      {s}
                    </span>
                    {/* <span className="text-sm font-semibold text-slate-600 group-hover:text-slate-800 transition-colors">
                      {STATE_NAMES[s]}
                    </span> */}
                  </button>
                ))}
              </div>
              {filteredStates.length === 0 && (
                <div className="text-center py-8 text-slate-300 text-sm">
                  No states found
                </div>
              )}
            </div>
          </>
        )}

        {/* Step 2 — School name search */}
        {step === 2 && (
          <>
            <div className="px-6 py-4 space-y-3">
              {/* Back button */}
              <button
                onClick={() => setStep(1)}
                className="flex items-center gap-1 text-xs text-slate-400 hover:text-slate-600 transition-colors"
              >
                ← Change state
              </button>
              <input
                ref={nameInputRef}
                value={nameQuery}
                onChange={e => setNameQuery(e.target.value)}
                placeholder={`Search schools in ${STATE_NAMES[selectedState]}...`}
                className="w-full p-3.5 rounded-2xl border-2 border-slate-100 focus:border-sky-400 outline-none transition-all placeholder:text-slate-300 text-sm bg-slate-50"
              />
            </div>

            <div className="flex-1 overflow-y-auto px-6 pb-6">
              {loading && (
                <div className="flex items-center justify-center py-12 text-slate-400 text-sm gap-2">
                  <span className="animate-spin text-lg">⏳</span> Searching...
                </div>
              )}
              {!loading && searched && results.length === 0 && (
                <div className="text-center py-12 text-slate-400 text-sm">
                  No schools found — try a different name
                </div>
              )}
              {!loading && !searched && (
                <div className="text-center py-12 text-slate-300 text-sm">
                  Start typing a school name above
                </div>
              )}
              {!loading && results.length > 0 && (
                <ul className="space-y-2">
                  {results.map(school => (
                    <li key={school.ncessch}>
                      <button
                        onClick={() => onSelect(school)}
                        className="w-full text-left p-4 rounded-2xl border-2 border-slate-100 hover:border-sky-400 hover:bg-sky-50 transition-all group"
                      >
                        <div className="font-semibold text-slate-800 text-sm group-hover:text-sky-600 transition-colors">
                          {school.name}
                        </div>
                        <div className="text-xs text-slate-400 mt-0.5">
                          {school.street} · {school.city}, {school.state} {school.zip}
                        </div>
                      </button>
                    </li>
                  ))}
                  {results.length === 30 && (
                    <p className="text-center text-xs text-slate-300 pt-2">
                      Showing first 30 — type more to narrow down
                    </p>
                  )}
                </ul>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
